import { useCallback, useContext, useEffect } from "react";
import { useDataLoadingReducer } from "./DataLoadingReducer";
import { ApiContext } from "../contexts/ApiContext";
import { MeasurementMetadata } from "../shared/interfaces/Data";
import { HistoricUTP2Data, HistoricUTP2Events } from "../shared/interfaces/UTP2";
import { Measurement, MeasurementDetail } from "../shared/interfaces/Result";
import { MeasurementComponent } from "../shared/enums/Result";

export function useHistoricUTP2Data<T extends HistoricUTP2Data | HistoricUTP2Events>(
  customerId: number,
  measurementDetail: MeasurementDetail<Measurement>,
  measurementsMetadata?: MeasurementMetadata[],
) {
  const [state, dispatch] = useDataLoadingReducer<T[] | undefined>(undefined);
  const apiContext = useContext(ApiContext);

  const fetchData = useCallback(async () => {
    dispatch({ type: "loading" });
    if (measurementsMetadata) {
      try {
        const response = await apiContext.dataApis.getDataByCapturesAndDataPointIds<string>(measurementsMetadata);
        if (response instanceof Error) {
          dispatch({ type: "errored", message: response.message });
        } else {
          const measurementHistories = await Promise.all(
            response.map(async (data) => {
              let index = 0;

              if (
                measurementDetail.measurement.ComponentType === MeasurementComponent.CableBox ||
                measurementDetail.measurement.ComponentType === MeasurementComponent.Spouts
              ) {
                index = await apiContext.resultsApis.getHistoricalPlotPosition(
                  customerId,
                  data[0].captureId,
                  data[0].dataPointId,
                  measurementDetail.measurementType,
                  measurementDetail.measurement.ComponentAssetId,
                  measurementDetail.measurement.SubLocation,
                );
              }

              if (index === undefined) {
                index = 0;
              }

              const measurementHistory: T = data && data.length > 0 && JSON.parse(data[index].dataValue);
              measurementHistory.captureId = data[index].captureId;
              return measurementHistory;
            }),
          );
          dispatch({ type: "loaded", data: measurementHistories });
        }
      } catch (error) {
        if (error instanceof Error) {
          dispatch({ type: "errored", message: error.message });
        } else {
          dispatch({ type: "errored", message: "An unknown error occurred" });
        }
      }
    }
  }, [dispatch, customerId, measurementDetail, measurementsMetadata, apiContext.dataApis, apiContext.resultsApis]);

  useEffect(() => {
    if (measurementsMetadata) {
      fetchData();
    }
  }, [measurementsMetadata, fetchData]);

  return { ...state, refreshData: fetchData };
}
